"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Dropdown = exports.closeEvents = exports.DropdownContext = void 0;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const app_1 = require("@obvio/app");
const utils_1 = require("@obvio/utils");
const react_1 = require("@obvio/utils/react");
const framer_motion_1 = require("framer-motion");
const react_2 = require("react");
const react_laag_1 = require("react-laag");
const Button_1 = require("../Button");
exports.DropdownContext = (0, react_2.createContext)(undefined);
exports.closeEvents = ['mousedown', 'dragover', 'resize', 'touchstart'];
const transitions = {
    initial: { opacity: 0, y: '-0.25rem' },
    active: { opacity: 1, y: '0rem' },
    exit: { opacity: 0, y: '-0.25rem' },
};
const StyledDropdownContainer = app_1.styled.div `
  z-index: 1000;
  overflow: hidden;
  border-radius: ${(theme) => theme.radius.small};
  box-shadow: ${(theme) => theme.shadows.medium};
  background-color: white;
  padding: 0.25rem 0;
  ${(theme) => theme.dropdown.css}
`;
const LaagWrapper = (0, app_1.styled)(framer_motion_1.motion.div) `
  z-index: 1000;
`;
exports.Dropdown = (0, react_2.forwardRef)(({ className, children, render: Render, placement, possiblePlacements, right = false, container, adherent = false, closeOnlySelf = false, disabled = false, context: styleContext, onOpen, onClose, }, ref) => {
    var _a, _b;
    const context = (0, react_2.useContext)(exports.DropdownContext);
    const clicking = (0, react_2.useRef)(false);
    const [open, setOpenInternal] = (0, react_2.useState)(false);
    const child = (0, react_2.useRef)(null);
    const remToPx = (0, app_1.useRemToPx)();
    const { type } = context !== null && context !== void 0 ? context : {};
    const setOpen = (0, react_2.useCallback)((...v) => {
        if (!disabled) {
            setOpenInternal(...v);
        }
    }, [disabled]);
    const close = (0, react_2.useCallback)(() => {
        onClose === null || onClose === void 0 ? void 0 : onClose();
        if (closeOnlySelf) {
            setOpen(false);
        }
        else if (context && context.type === 'menu') {
            context.close();
        }
        else {
            setOpen(false);
        }
    }, [closeOnlySelf, context, onClose, setOpen]);
    (0, react_2.useImperativeHandle)(ref, () => ({
        close() {
            close();
        },
    }));
    const handleWindowEvent = (0, react_2.useCallback)((ev) => {
        const { current } = clicking;
        clicking.current = false;
        if (['mousedown', 'touchstart'].includes(ev.type) && current) {
            return;
        }
        if (type === 'menu') {
            close();
        }
    }, [close, type]);
    const setupCloseListeners = (0, react_2.useCallback)(() => {
        exports.closeEvents.forEach((ev) => window.addEventListener(ev, handleWindowEvent));
        return () => {
            exports.closeEvents.forEach((ev) => window.removeEventListener(ev, handleWindowEvent));
        };
    }, [handleWindowEvent]);
    (0, react_2.useEffect)(() => {
        if (open) {
            return setupCloseListeners();
        }
    }, [open, setupCloseListeners]);
    const toggleAction = (0, react_2.useCallback)((ev) => {
        clicking.current = true;
        ev.stopPropagation();
        setOpen((v) => {
            clicking.current = false;
            if (v) {
                onClose === null || onClose === void 0 ? void 0 : onClose();
            }
            else {
                onOpen === null || onOpen === void 0 ? void 0 : onOpen();
            }
            return !v;
        });
    }, [onClose, onOpen, setOpen]);
    const onFocus = (0, react_2.useCallback)(() => {
        if (!clicking.current && !open) {
        }
    }, [open]);
    const { renderLayer, triggerProps, layerProps, layerSide } = (0, react_laag_1.useLayer)(Object.assign(Object.assign({ isOpen: open, overflowContainer: true, auto: true, snap: true }, (container && { container })), { placement: placement !== null && placement !== void 0 ? placement : (type ? 'right-start' : 'bottom-start'), triggerOffset: adherent ? 0 : remToPx(0.5), possiblePlacements: possiblePlacements !== null && possiblePlacements !== void 0 ? possiblePlacements : (type
            ? ['left-start', 'right-start']
            : ['bottom-center', 'top-center', 'left-center', 'right-center']), onOutsideClick: () => close(), onParentClose: () => close() }));
    const rootBoundingClient = (_a = child.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
    const menuContextValue = (0, react_2.useMemo)(() => ({ type: 'menu', close }), [close]);
    const dropdownContainerCss = (0, react_2.useMemo)(() => {
        const top = (theme) => adherent && layerSide === 'bottom' ? 0 : theme.radius.small;
        const bottom = (theme) => adherent && layerSide === 'top' ? 0 : theme.radius.small;
        return (0, app_1.css) `
        border-top-left-radius: ${top};
        border-top-right-radius: ${top};
        border-bottom-left-radius: ${bottom};
        border-bottom-right-radius: ${bottom};
        border: ${(theme) => adherent && `0.0625rem solid ${theme.stroke}`};
      `;
    }, [adherent, layerSide]);
    const element = renderLayer((0, jsx_runtime_1.jsx)(framer_motion_1.AnimatePresence, { children: open && ((0, jsx_runtime_1.jsx)(LaagWrapper, Object.assign({}, layerProps, { initial: "initial", animate: "active", exit: "exit", variants: transitions, transition: {
                duration: 0.15,
            } }, { children: (0, jsx_runtime_1.jsx)(StyledDropdownContainer, Object.assign({ "data-role": "dropdown", css: dropdownContainerCss, className: className, style: (0, utils_1.exclude)({
                    width: adherent ? rootBoundingClient === null || rootBoundingClient === void 0 ? void 0 : rootBoundingClient.width : 'unset',
                    minWidth: right && child.current
                        ? (_b = child.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().width
                        : 'unset',
                }, undefined) }, { children: (0, jsx_runtime_1.jsx)(exports.DropdownContext.Provider, Object.assign({ value: menuContextValue }, { children: (0, jsx_runtime_1.jsx)(Render, { close: close }) })) })) }))) }));
    const triggerContextValue = (0, react_2.useMemo)(() => ({ type: 'trigger' }), []);
    const Wrap = (0, react_2.useCallback)(({ children: content }) => styleContext ? ((0, jsx_runtime_1.jsx)(app_1.Context, Object.assign({ name: styleContext }, { children: content }))) : ((0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: content })), [styleContext]);
    const childElement = (0, react_2.cloneElement)(typeof children === 'function'
        ? children({ open, layerSide, close })
        : children, {
        onMouseDown: (ev) => {
            toggleAction(ev);
        },
        onFocus,
        ref: (0, react_1.mergeRefs)(child, triggerProps.ref),
    });
    return ((0, jsx_runtime_1.jsxs)(Wrap, { children: [(0, jsx_runtime_1.jsx)(exports.DropdownContext.Provider, Object.assign({ value: triggerContextValue }, { children: childElement })), element] }));
});
const ElementButton = (0, app_1.styled)(Button_1.Button) `
  border-radius: 0 !important;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  text-align: left;
  border: none;
  padding: 0;
  padding-left: ${(theme) => theme.spacing.small};
  padding-right: ${(theme) => theme.spacing.medium};
  font-size: 0.875rem;
  color: ${(theme) => theme.colors.gray.semidark};
  &:hover {
    background: ${(theme) => theme.colors.gray.intense};
  }

  ${(theme) => theme.dropdown.element}
`;
const Element = (0, react_2.forwardRef)((_a, ref) => {
    var { onClick, children } = _a, props = __rest(_a, ["onClick", "children"]);
    const context = (0, react_2.useContext)(exports.DropdownContext);
    const handleClick = (0, react_2.useCallback)((ev) => {
        if ((context === null || context === void 0 ? void 0 : context.type) === 'menu') {
            context.close();
        }
        onClick === null || onClick === void 0 ? void 0 : onClick(ev);
    }, [context, onClick]);
    return ((0, jsx_runtime_1.jsx)(ElementButton, Object.assign({ ref: ref, kind: "transparent", onClick: handleClick }, props, { children: children })));
});
exports.Dropdown.Element = Element;
